import React, { useState, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import useTicketSearch from '../../../hooks/useTicketSearch';
import TicketFilterBar from '../filters/TicketFilterBar';
import Pagination from '../common/Pagination';
import TicketTable from '../common/TicketTable';
import PhaseListPanel from '../phases/PhaseListPanel';
import PhaseCreateModal from '../phases/PhaseCreateModal';
import MyDeliverables from '../phases/MyDeliverables';
import './TicketListView.scss';

const TicketListView = ({ projectId = null, onNavigate, refreshKey = 0, initialTab = 'tickets', initialPhaseId = null }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { statusOptions, companyUsers, usersMapByEmail, projects } = useBetaData();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);

  // 模式判斷：project 或 my-tasks
  const userId = projectId ? null : profile?.id;
  // viewingCompanyId === undefined 代表 AuthContext 尚未初始化完成，不要帶 companyId
  const companyId = viewingCompanyId === undefined ? undefined : (viewingCompanyId || profile?.company_id);

  const {
    tickets,
    totalCount,
    loading,
    search,
    handleSearchChange,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    assigneeFilter,
    setAssigneeFilter,
    projectFilter,
    setProjectFilter,
    page,
    setPage,
    totalPages,
    from,
    to,
    sortBy,
    sortOrder,
    handleSort,
    activeFilterCount,
    clearAllFilters,
  } = useTicketSearch({ projectId, userId, companyId, viewerUserId: profile?.id, statusOptions: statusOptions || [], projects: !projectId ? (projects || []) : [], refreshKey });

  // 用 companyUsers 建立 userId → user info 的 map（含 department_name）
  const userMap = useMemo(() => {
    const map = {};
    (companyUsers || []).forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [companyUsers]);

  const handleRowClick = (ticket) => {
    onNavigate('ticket', ticket.project_id, ticket.id, { ticketNo: ticket.ticket_no });
  };

  // 專案頁面：顯示 Tab 切換（Tickets / Phases）
  if (projectId && activeTab === 'phases') {
    return (
      <div className="ticket-list-view">
        <div className="tlv-tabs">
          <button
            className={`tlv-tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            Tickets
          </button>
          <button
            className={`tlv-tab ${activeTab === 'phases' ? 'active' : ''}`}
            onClick={() => setActiveTab('phases')}
          >
            Phases
          </button>
          <button
            className="tlv-tab-action"
            onClick={() => { setEditingPhase(null); setShowPhaseModal(true); }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 1v10M1 6h10" strokeLinecap="round" />
            </svg>
            新增 Phase
          </button>
        </div>
        <PhaseListPanel
          projectId={projectId}
          initialPhaseId={initialPhaseId}
          onEditPhase={(phase) => { setEditingPhase(phase); setShowPhaseModal(true); }}
        />
        <PhaseCreateModal
          isOpen={showPhaseModal}
          onClose={() => { setShowPhaseModal(false); setEditingPhase(null); }}
          onSuccess={() => {}}
          phase={editingPhase}
          projectId={projectId}
        />
      </div>
    );
  }

  const emptyState = (
    <div className="list-empty-state">
      {activeFilterCount > 0 || search ? (
        <>
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#ccc" strokeWidth="1.5">
              <circle cx="20" cy="20" r="14" />
              <path d="M30 30l10 10" strokeLinecap="round" />
            </svg>
          </div>
          <p className="empty-title">沒有符合條件的 Ticket</p>
          <p className="empty-desc">試試調整搜尋條件或篩選設定</p>
          <button className="empty-clear-btn" onClick={clearAllFilters}>
            清除所有篩選
          </button>
        </>
      ) : (
        <>
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#ccc" strokeWidth="1.5">
              <rect x="8" y="8" width="32" height="32" rx="4" />
              <path d="M16 24l6 6 10-12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="empty-title">尚無 Ticket</p>
          {/* 全面封鎖建立 Ticket,按鈕已隱藏,引導文案一併拿掉
          <p className="empty-desc">點擊上方「建立 Ticket」按鈕來新增</p> */}
        </>
      )}
    </div>
  );

  return (
    <div className="ticket-list-view">
      {projectId && (
        <div className="tlv-tabs">
          <button
            className={`tlv-tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            Tickets
          </button>
          <button
            className={`tlv-tab ${activeTab === 'phases' ? 'active' : ''}`}
            onClick={() => setActiveTab('phases')}
          >
            Phases
          </button>
        </div>
      )}
      {!projectId && <MyDeliverables onNavigate={onNavigate} />}
      <TicketFilterBar
        search={search}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={setPriorityFilter}
        assigneeFilter={assigneeFilter}
        onAssigneeFilterChange={setAssigneeFilter}
        projectFilter={projectFilter}
        onProjectFilterChange={!projectId ? setProjectFilter : undefined}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
        statusOptions={statusOptions || []}
        companyUsers={companyUsers}
        projects={!projectId ? (projects || []) : []}
        hideAssigneeFilter={!projectId}
        searchPlaceholder={!projectId ? '搜尋我的任務... (#編號 搜尋指定任務)' : undefined}
      />

      <TicketTable
        tickets={tickets}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleSort={handleSort}
        onRowClick={handleRowClick}
        statusOptions={statusOptions}
        userMap={userMap}
        usersMapByEmail={usersMapByEmail}
        showProject={!projectId}
        emptyState={emptyState}
      />

      {!loading && tickets.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          from={from}
          to={to}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default TicketListView;
